/**
 * The aspect judge (band C) — the ONLY model opinion in the Category Cockpit.
 *
 * Everything it renders is VIOLET, and the colour is load-bearing, exactly as in the product
 * cockpit's JudgePanel: plain = verbatim from the database, orange = computed by the cockpit,
 * violet = a model has an OPINION about it. A reader must never have to guess which.
 *
 * It runs ON DEMAND and behind a confirmation that names the spend and the write, because this
 * cockpit writes nothing without one (Act.tsx rule 1) and the judge both pays a provider and stores
 * a row. The call outlives the edge timeout, so a start answers at once and the panel POLLS the
 * run; a run still in flight when the screen is re-opened is picked back up, never paid twice.
 * Its findings are offered as ADD / REVISE suggestions, the prototype's QA panel shape. Their
 * Apply files a DRAFT (band D): the chapter supplies the control (`applyFor`), which opens the
 * finding as an editable sentence and files it for approval — nothing is written to the aspect
 * until a super admin approves it. Rendered without it, Apply is inert and says so.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAspectJudgeRun, fetchAspectJudgement, runAspectJudge,
  type Aspect, type AspectJudgeFailure, type AspectJudgement, type AspectJudgeVerdict,
} from '@/services/categoryCockpitService';

/** how often a run in flight is re-read — the judge takes one to three minutes */
export const POLL_MS = 4000;
/** past this, the server's reservation window (15 min) has marked the run abandoned — stop asking */
export const POLL_GIVE_UP_MS = 16 * 60 * 1000;

const VERDICT_LABEL: Record<AspectJudgeVerdict, string> = {
  complete: 'nothing missing against the recipe',
  minor_gaps: 'minor gaps',
  major_gaps: 'MAJOR gaps',
  // NOT a fourth severity: rendering it blank would read a rejected assessment as "no gaps"
  indeterminate: 'verdict unsupported — nothing to stand on',
};

const JUDGE_TIP = 'LLM judge — a guided review of this aspect against an editable recipe '
  + '(config/cockpit-category-judge-recipes.json), set against the catalogue\'s real products, brands and prices. '
  + 'It may only report elements listed in the recipe, every finding must quote its evidence, and anything '
  + 'off-recipe is discarded and counted. It is a model OPINION, never a measurement.';

const APPLY_TIP = 'Apply files a DRAFT of this change for approval in To decide (band D): you edit the sentence first, '
  + 'it is parsed (one model call) and nothing is written to the category until a super admin approves it.';
const NO_APPLY_TIP = 'Drafting is not offered on this screen.';

/** one finding, as the judge worded it — what an Apply turns into a draft sentence */
export interface JudgeFinding { op: 'add' | 'revise'; element: string; detail: string | null; evidence: string }

/** the sentence an Apply starts from: the finding in the chat's own vocabulary, for the operator to edit */
export function findingSentence(aspectLabel: string, f: JudgeFinding): string {
  return f.op === 'add'
    ? `Add to ${aspectLabel.toLowerCase()}: ${f.element}`
    : `Correct in ${aspectLabel.toLowerCase()}: ${f.element}${f.detail ? ` — ${f.detail}` : ''}`;
}

const day = (s: string | null | undefined) => String(s ?? '').slice(0, 10);

const elapsed = (since: number, now: number) => {
  const s = Math.max(0, Math.round((now - since) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
};

interface InFlight { id: string; since: number; joined: boolean }

interface Props {
  sectorId: number;
  sectorName: string;
  path: string;
  aspect: Aspect;
  aspectLabel: string;
  /** why the aspect cannot be judged (never researched, empty) — the button says so instead of failing */
  unavailable?: string | null;
  /**
   * the Apply control of one finding (band D: a draft). Supplied by the chapter, which holds the
   * action context and the selected site; without it the Apply button is shown inert.
   */
  applyFor?: (finding: JudgeFinding, sentence: string) => React.ReactNode;
}

export function AspectJudge({ sectorId, sectorName, path, aspect, aspectLabel, unavailable, applyFor }: Props) {
  const [judgement, setJudgement] = useState<AspectJudgement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [starting, setStarting] = useState(false);
  const [inFlight, setInFlight] = useState<InFlight | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  // an attempt that failed while nobody was watching — shown on return, never swallowed
  const [lastFailure, setLastFailure] = useState<AspectJudgeFailure | null>(null);

  // A slow initial GET could land AFTER a start and overwrite it with an older judgement, or
  // resume a run that is no longer the newest. Every request takes a ticket; an answer older than
  // one already applied is dropped.
  const seqRef = useRef(0);
  const appliedRef = useRef(0);
  const ticket = () => { seqRef.current += 1; return seqRef.current; };
  const current = (seq: number) => {
    if (seq < appliedRef.current) return false;
    appliedRef.current = seq;
    return true;
  };

  useEffect(() => {
    const ac = new AbortController();
    const seq = ticket();
    fetchAspectJudgement(sectorId, path, aspect, ac.signal)
      .then((state) => {
        setLoaded(true);
        if (!current(seq)) return;
        setJudgement(state.judgement);
        setLastFailure(state.last_failure ?? null);
        // left and re-opened while the judge ran: pick the run back up instead of offering a
        // button that would only join it
        if (state.in_flight) setInFlight({ id: state.in_flight.id, since: Date.parse(state.in_flight.created_at) || Date.now(), joined: true });
      })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // "not judged yet" is judgement:null, NOT an error — so this is a real read failure
        // (before the migration has run, for instance) and must not be presented as "not judged"
        setReadError((err as Error)?.message ?? String(err));
        setLoaded(true);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, path, aspect]);

  // the poll: one run at a time, until it settles, the window expires, or the panel goes away
  const runId = inFlight?.id;
  const since = inFlight?.since;
  useEffect(() => {
    if (!runId || since == null) return undefined;
    const ac = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      setNow(Date.now());
      if (Date.now() - since > POLL_GIVE_UP_MS) {
        setInFlight(null);
        setError(`no result after ${Math.round(POLL_GIVE_UP_MS / 60000)} min — the run will be recorded as abandoned (it stays counted against the daily cap). Re-open this screen later, or run it again.`);
        return;
      }
      const seq = ticket();
      try {
        const r = await fetchAspectJudgeRun(sectorId, path, aspect, runId, ac.signal);
        setPollError(null);
        if (r.status === 'running') { timer = setTimeout(tick, POLL_MS); return; }
        if (!current(seq)) return;
        setInFlight(null);
        // ⛔ ONE FAILURE DISPLAY, WHICHEVER WAY IT WAS SEEN. Watching it fail kept only the error
        // and dropped the recorded spend, until the panel was reopened (codex dual-review, #6531).
        if (r.status === 'complete') { setLastFailure(null); setJudgement(r); } else setLastFailure(r);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // the RUN is unaffected by a failed read of it — say so and keep asking
        setPollError((err as Error)?.message ?? String(err));
        timer = setTimeout(tick, POLL_MS);
      }
    };
    timer = setTimeout(tick, POLL_MS);
    return () => { ac.abort(); if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, since, sectorId, path, aspect]);

  const run = useCallback(async () => {
    setConfirming(false);
    setStarting(true);
    setError(null);
    setLastFailure(null);
    const seq = ticket();
    try {
      const r = await runAspectJudge(sectorId, path, aspect, { force: !!judgement });
      if (!current(seq)) return;
      if (r.status === 'complete') setJudgement(r);
      else if (r.status === 'running') {
        setNow(Date.now());
        setInFlight({ id: r.id, since: Date.parse(r.created_at) || Date.now(), joined: !!r.joined });
      } else setLastFailure(r);
    } catch (err) {
      setError((err as Error)?.message ?? String(err));
    } finally {
      setStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId, path, aspect, judgement]);

  const running = starting || !!inFlight;

  const section = judgement?.verdicts?.[aspect];
  const s = judgement?.summary;
  const suggestions = section
    ? [
      ...section.missing.map((m) => ({ op: 'add' as const, element: m.element, detail: null as string | null, evidence: m.evidence, severity: m.severity })),
      ...section.weak.map((w) => ({ op: 'revise' as const, element: w.element, detail: w.why || null, evidence: w.evidence, severity: null })),
    ]
    : [];

  return (
    <div className="pmk-judge" data-testid="aspect-judge">
      <div className="pmc-judgebar">
        <span className="pmc-sbadge pmc-judge" data-tip={JUDGE_TIP}>LLM judge</span>
        <button
          type="button"
          className="pmc-judgebtn"
          disabled={running || confirming || !!unavailable}
          title={unavailable || undefined}
          onClick={() => setConfirming(true)}
        >
          {starting ? '⚖ starting…' : inFlight ? '⚖ judging…' : judgement ? '⚖ re-run the judge' : '⚖ judge this aspect'}
        </button>
        {inFlight && (
          <span className="pmc-small pmc-mute" data-testid="judge-in-flight">
            {`${inFlight.joined ? 'a run already in progress' : 'running'} · ${elapsed(inFlight.since, now)} · usually 1–3 min — you can leave this screen; the run continues and is picked back up here`}
          </span>
        )}
        {unavailable && <span className="pmc-small pmc-mute">{unavailable}</span>}
        {section && (
          <span className="pmc-small">
            <span className={`pmc-jverdict pmc-jv-${section.verdict}`}>{VERDICT_LABEL[section.verdict]}</span>
            <span className="pmc-mute">
              {` · ${judgement!.model} · ${day(judgement!.created_at)}`}
              {judgement!.cost_usd != null ? ` · $${Number(judgement!.cost_usd).toFixed(4)}` : ''}
              {judgement!.recipes_version ? ` · recipe ${judgement!.recipes_version}` : ''}
            </span>
          </span>
        )}
        {!judgement && !running && !lastFailure && loaded && !readError && !unavailable && <span className="pmc-small pmc-mute">not judged yet — nothing is judged unless you ask</span>}
      </div>

      {confirming && (
        <div className="pmk-judge-confirm" role="dialog" aria-label={`Judge ${aspectLabel}`}>
          <b>{judgement ? 'Re-run the judge' : 'Judge'} on {aspectLabel} · {path.split(' > ').slice(-1)[0]} · {sectorName}</b>
          <dl className="pmk-kv">
            <dt>writes</dt><dd>one row in <code>cockpit_category_judgements</code> — the verdict, its findings and the spend. The aspect itself is not touched.</dd>
            <dt>costs</dt><dd>one call to <code>claude-sonnet-4-6</code>, a few cents and under about $0.10 (its reply is capped at 3,000 tokens) · capped at 50 runs per sector per day{judgement ? ' · re-running pays again even when nothing changed' : ''}</dd>
            <dt>reads</dt><dd>this aspect, the other aspects' names, the category's perceived attributes, and the catalogue of the sector's sites for this category (brands, prices per currency, extracted attributes, 30 product names)</dd>
            <dt>takes</dt><dd>usually one to three minutes — the panel follows the run, and you can leave the screen meanwhile</dd>
          </dl>
          <div className="pmk-judge-confirm-btns">
            <button type="button" className="pmc-judgebtn" onClick={run}>{judgement ? 'Re-run' : 'Judge'}</button>
            <button type="button" className="pmc-btn" onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      )}

      {s?.content_truncated && (
        <div className="pmc-small pmc-syn">
          {`⚠ the judge read ${s.content_truncated.shown} of ${s.content_truncated.total} entries — the aspect is longer than its budget. The rest was NOT judged.`}
        </div>
      )}
      {s?.catalogue_empty && (
        <div className="pmc-small pmc-syn">⚠ no product of the sector's sites is filed under this category — the judge had no catalogue to check the aspect against.</div>
      )}
      {s?.price_comparable === false && (
        <div className="pmc-small pmc-syn">⚠ the category's prices span several currencies, or some carry none — the judge was told to compare prices only within one currency, never across.</div>
      )}
      {!!judgement?.discarded_count && (
        <div className="pmc-small pmc-syn" data-tip="The judge produced findings outside this aspect's recipe, or without evidence. Validation discarded them — this is how many.">
          {`⚠ ${judgement.discarded_count} off-recipe finding${judgement.discarded_count === 1 ? '' : 's'} discarded`}
        </div>
      )}
      {judgement?.stale === true && (
        <div className="pmc-small pmc-jstale">⚠ judged different evidence — the aspect, its catalogue or its recipe changed since. The findings may not describe what is on screen; re-run to judge it now.</div>
      )}
      {judgement && judgement.staleness_known === false && (
        <div className="pmc-small pmc-mute">freshness unknown — today's aspect could not be read as evidence, so this verdict is not claimed to be current</div>
      )}
      {judgement?.reused && <div className="pmc-small pmc-mute">reused — the evidence has not changed since the last run, nothing was paid</div>}

      {section && (suggestions.length > 0 || section.verdict_note || section.note) && (
        <div className="pmc-jblock">
          {suggestions.map((x, i) => (
            <div className="pmc-jrow" key={`${x.op}-${i}`}>
              <span className={`pmc-jtag ${x.op === 'add' ? `pmc-${x.severity}` : 'pmc-weak-tag'}`}>
                {x.op === 'add' ? `ADD · ${x.severity}` : 'REVISE'}
              </span>
              <span>
                <b>{x.element}</b>{x.detail ? <span className="pmc-small"> — {x.detail}</span> : null}<br />
                <span className="pmc-small pmc-mute">{x.evidence}</span>
              </span>
              {applyFor
                ? <span className="pmk-judge-apply">{applyFor(x, findingSentence(aspectLabel, x))}</span>
                : <button type="button" className="pmc-btn pmk-judge-apply" disabled title={NO_APPLY_TIP} aria-label={`Apply: ${x.element} (not offered here)`}>Apply</button>}
            </div>
          ))}
          {section.verdict_note && <div className="pmc-small pmc-jindet">{section.verdict_note}</div>}
          {section.note && <div className="pmc-small pmc-jnote"><b className="pmc-mute">judge note · </b>{section.note}</div>}
          {suggestions.length > 0 && <div className="pmc-small pmc-mute" style={{ marginTop: 6 }}>{applyFor ? APPLY_TIP : NO_APPLY_TIP}</div>}
        </div>
      )}

      {lastFailure && !inFlight && (
        <div className="pmc-small pmc-jerr" data-testid="judge-last-failure">
          {lastFailure.abandoned
            ? `the last run (${day(lastFailure.created_at)}) was abandoned: no result was recorded within the reservation window`
            : `the last run (${day(lastFailure.created_at)}) failed: ${lastFailure.error || 'no reason recorded'}`}
          {/* ⛔ A NULL COST PROVES NOTHING: a reply with unmeasurable usage, a timeout, a process that
              died mid-call all leave it null, and each may have been billed. Only a recorded cost
              is a statement about spend (codex dual-review, #6531). */}
          {lastFailure.cost_usd != null
            ? ` · it was billed $${Number(lastFailure.cost_usd).toFixed(4)}`
            : ' · no spend recorded — billing unknown'}
          {judgement ? ' — the verdict shown is from an earlier run' : ''}
        </div>
      )}
      {error && <div className="pmc-small pmc-jerr">judge failed: {error}</div>}
      {inFlight && pollError && <div className="pmc-small pmc-jerr">could not read the run's progress ({pollError}) — the run itself is unaffected; still asking</div>}
      {readError && <div className="pmc-small pmc-jerr">could not read a stored judgement: {readError}</div>}
    </div>
  );
}
